import { Platform } from 'react-native';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../utilities/apiClient';
import { getSessionIds, getStoreState } from './_sessionIds';
import debug from '../../utilities/debug';
import { SET_CALENDAR_STATUS, SET_CALENDAR_EVENTS, SET_CALENDAR_LOADING, SET_TIMELINE_EVENTS, INVALIDATE_TIMELINE } from '../constants';

export const fetchCalendarStatus = (projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiGet(`/calendar/status/${projectId}/${accountId}/${cognitoId}`);
    const status = res?.payload ?? (res && (typeof res.connected !== 'undefined' || res.performed_sync !== undefined) ? res : null);
    dispatch({ type: SET_CALENDAR_STATUS, payload: { projectId: String(projectId), status } });
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

const TIMELINE_STALE_MS = 5 * 60 * 1000; // 5 minutes

export const fetchTimeline = (
  projectId: number | string,
  options?: { includeTypes?: string[]; startDate?: string; endDate?: string; force?: boolean }
) => async (dispatch: any): Promise<{ success: boolean; payload?: { events: any[]; count: number }; message?: string; fromCache?: boolean }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };

  const projectKey = String(projectId);
  const state = (getStoreState() as any).calendar;
  const cachedEvents: any[] | undefined = state?.timeline?.[projectKey];
  const fetchedAt: number = state?.timelineFetched?.[projectKey] ?? 0;
  const isStale = Date.now() - fetchedAt > TIMELINE_STALE_MS;

  // Return cached data immediately if fresh enough (unless force-refreshing)
  if (!options?.force && cachedEvents && !isStale) {
    return { success: true, payload: { events: cachedEvents, count: cachedEvents.length }, fromCache: true };
  }

  try {
    const qs = new URLSearchParams();
    if (options?.includeTypes?.length) qs.set('include_types', options.includeTypes.join(','));
    if (options?.startDate) qs.set('start_date', options.startDate);
    if (options?.endDate) qs.set('end_date', options.endDate);
    const query = qs.toString() ? `?${qs}` : '';
    const res = await apiGet(`/timeline/get/${projectId}/${accountId}/${cognitoId}${query}`);
    if (res?.success && Array.isArray(res?.payload?.events)) {
      dispatch({
        type: SET_TIMELINE_EVENTS,
        payload: { projectId: projectKey, events: res.payload.events, fetchedAt: Date.now() },
      });
    }
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchCalendarEvents = (projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  dispatch({ type: SET_CALENDAR_LOADING, payload: true });
  try {
    const res = await apiGet(`/calendar/get-events/${projectId}/${accountId}/${cognitoId}`);
    const events = res?.payload ?? res?.events ?? [];
    dispatch({ type: SET_CALENDAR_EVENTS, payload: { projectId: String(projectId), events } });
    return res;
  } catch (err: any) {
    dispatch({ type: SET_CALENDAR_EVENTS, payload: { projectId: String(projectId), events: [] } });
    return { success: false, message: err?.message };
  } finally {
    dispatch({ type: SET_CALENDAR_LOADING, payload: false });
  }
};

export const saveCalendarToken = (projectId: number | string, code: string, redirectUri?: string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const body: Record<string, string> = { code };
    if (redirectUri) body.redirect_uri = redirectUri;
    const res = await apiPost(`/calendar/save-token/${projectId}/${accountId}/${cognitoId}`, body);
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const claimCalendarToken = (
  projectId: number | string,
  claim: string,
  options?: { isAdditional?: boolean },
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    debug.log('calendar', 'claimCalendarToken missing session ids', { hasAccountId: !!accountId, hasCognitoId: !!cognitoId });
    return { success: false };
  }
  const path = `/calendar/claim-token/${projectId}/${accountId}/${cognitoId}`;
  debug.log('calendar', 'claimCalendarToken POST', { path, claimLen: claim?.length, isAdditional: !!options?.isAdditional });
  try {
    const res = await apiPost(path, { claim, is_additional: options?.isAdditional ?? false });
    debug.log('calendar', 'claimCalendarToken response', { success: res?.success, message: res?.message, token_id: res?.token_id ?? null });
    return res;
  } catch (err: any) {
    debug.error('claimCalendarToken failed', err);
    return { success: false, message: err?.message };
  }
};

export const triggerCalendarSync = (projectId: number | string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiGet(`/calendar/trigger-sync/${projectId}/${accountId}/${cognitoId}`);
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const disconnectCalendar = (projectId: number | string, tokenId?: number) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const body: Record<string, any> = {};
    if (tokenId) body.token_id = tokenId;
    const res = await apiPost(`/calendar/disconnect/${projectId}/${accountId}/${cognitoId}`, body);
    // If disconnecting a specific token, only clear status if no other tokens remain.
    // The caller is responsible for refreshing status after a per-account disconnect.
    if (!tokenId) {
      dispatch({ type: SET_CALENDAR_STATUS, payload: { projectId: String(projectId), status: null } });
      dispatch({ type: SET_CALENDAR_EVENTS, payload: { projectId: String(projectId), events: [] } });
    }
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const createCalendarEvent = (projectId: number | string, data: {
  summary: string;
  description?: string;
  start: string;
  end: string;
  calendar_id?: string | null;
  provider?: string;
  attendees?: { email: string }[];
}) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPost(
      `/calendar/create-event/${projectId}/${accountId}/${cognitoId}`,
      data
    );
    return res?.payload !== undefined ? { ...res, payload: res.payload } : res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const updateCalendarEvent = (projectId: number | string, eventId: string, data: {
  summary?: string;
  description?: string;
  start?: string;
  end?: string;
  new_calendar_id?: string;
  attendees?: { email: string }[];
}) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPatch(
      `/calendar/update-event/${projectId}/${accountId}/${cognitoId}/${encodeURIComponent(eventId)}`,
      data
    );
    return res?.payload !== undefined ? { ...res, payload: res.payload } : res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const deleteCalendarEvent = (projectId: number | string, eventId: string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiDelete(
      `/calendar/delete-event/${projectId}/${accountId}/${cognitoId}/${encodeURIComponent(eventId)}`
    );
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const listCalendars = (projectId: number | string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiGet(`/calendar/list-calendars/${projectId}/${accountId}/${cognitoId}`);
    const calendars = res?.calendars ?? res?.payload?.calendars ?? [];
    const selections = res?.selections ?? res?.payload?.selections ?? [];
    const settings = res?.settings ?? res?.payload?.settings ?? {};
    return { success: !!res?.success, calendars, selections, settings };
  } catch (err: any) {
    return { success: false, calendars: [], selections: [], settings: {} };
  }
};

export const selectCalendars = (
  projectId: number | string,
  selected: string[],
  defaultCalendarId: string | null,
  settings: Record<string, unknown>,
  shares: Record<string, boolean>,
  colors: Record<string, string>,
  calendarEventRemindersEnabled?: boolean,
  calendarShowAllEvents?: boolean
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPost(
      `/calendar/select-calendars/${projectId}/${accountId}/${cognitoId}`,
      {
        selected,
        default_calendar_id: defaultCalendarId,
        settings,
        shares,
        colors,
        calendar_event_reminders_enabled: calendarEventRemindersEnabled,
        calendar_show_all_events: calendarShowAllEvents,
      }
    );
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// Read all per-calendar timeline-inclusion flags for a project. Optional
// provider filter ('google' | 'apple' | 'local') narrows the response.
export const fetchCalendarTimelineInclusions = (
  projectId: number | string,
  provider?: 'google' | 'apple' | 'local',
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, inclusions: [] as any[] };
  try {
    const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
    const res = await apiGet(
      `/calendar/update-timeline-inclusion/${projectId}/${accountId}/${cognitoId}${qs}`,
    );
    return res;
  } catch (err: any) {
    return { success: false, inclusions: [], message: err?.message };
  }
};

// Toggle whether a specific calendar's events appear on the Timeline view.
// Default FALSE (all calendars) per product spec; auto-ingested events
// created by extraction or a patient agent bypass this flag server-side.
export const updateCalendarTimelineInclusion = (
  projectId: number | string,
  provider: 'google' | 'apple' | 'local',
  calendarId: string,
  includeInTimeline: boolean,
) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPatch(
      `/calendar/update-timeline-inclusion/${projectId}/${accountId}/${cognitoId}`,
      { provider, calendar_id: calendarId, include_in_timeline: includeInTimeline },
    );
    if (res?.success !== false) {
      // Flipping this flag changes which events appear on the Timeline
      // view. Bust the client-side Redux timeline cache so the next
      // CalendarScreen focus / pull-to-refresh re-fetches from the server
      // (whose cache was also invalidated server-side). Without this the
      // stale `timeline[projectId]` entry satisfies `!isStale` inside
      // `fetchTimeline` and we keep showing the pre-toggle event set.
      dispatch({ type: INVALIDATE_TIMELINE, payload: { projectId: String(projectId) } });
    }
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchManualSources = (projectId: number | string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, sources: [] };
  try {
    const res = await apiGet(`/calendar/manual-sources/${projectId}/${accountId}/${cognitoId}`);
    return res;
  } catch (err: any) {
    return { success: false, sources: [], message: err?.message };
  }
};

export const removeManualSource = (projectId: number | string, source: string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiDelete(
      `/calendar/manual-sources/${projectId}/${accountId}/${cognitoId}?source=${encodeURIComponent(source)}`
    );
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// Add a manual calendar source by URL — an ICS/webcal feed or a public
// Google Calendar share link. The backend normalizes Google share URLs
// (?src= / ?cid= / base64 cid) itself; callers only pick the type. For
// type 'ics' the server requires an https URL ending in .ics, so
// webcal:// links must be normalized to https:// before calling this.
export const addManualSource = (
  projectId: number | string,
  input: { source: string; type: 'ics' | 'google_public'; calendar_name?: string },
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPost(
      `/calendar/manual-sources/${projectId}/${accountId}/${cognitoId}`,
      input,
    );
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchTimelineInsights = (projectId: number | string) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiGet(`/timeline/insights/${projectId}/${accountId}/${cognitoId}`);
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const exportTimelineReport = (
  projectId: number | string,
  options?: { period?: string; format?: 'markdown' | 'json' }
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const qs = new URLSearchParams();
    if (options?.period) qs.set('period', options.period);
    if (options?.format) qs.set('format', options.format);
    const query = qs.toString() ? `?${qs}` : '';
    const res = await apiGet(`/timeline/export/${projectId}/${accountId}/${cognitoId}${query}`);
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ── Apple Calendar ────────────────────────────────────────────────────────────

/**
 * Register (or remove) an Apple Calendar connection for a project.
 * Called once when the user grants permission (action='connect') or taps Remove (action='disconnect').
 */
export const registerAppleCalendarConnection = (
  projectId: number | string,
  action: 'connect' | 'disconnect' | 'update-calendars',
  calendars: { id: string; name: string; isPrimary?: boolean; selected?: boolean }[] = [],
  deviceId?: string,
) => async (_dispatch: any) => {
  if (Platform.OS !== 'ios') return { success: false, message: 'iOS only' };
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPost(
      `/calendar/register-apple-connection/${projectId}/${accountId}/${cognitoId}`,
      { action, calendars, device_id: deviceId },
    );
    return res;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/**
 * Run a full Apple Calendar sync for a project (reads device, posts to backend,
 * refreshes Redux events).  Pass fullSync=true to ignore delta timestamp.
 */
export const syncAppleCalendar = (
  projectId: number | string,
  fullSync = false,
) => async (dispatch: any) => {
  if (Platform.OS !== 'ios') return { success: false, skipped: true };
  try {
    const { runAppleCalendarSync } = require('../../utilities/appleCalendarSync');
    const result = await runAppleCalendarSync(String(projectId), fullSync);
    if (!result.skipped && result.synced >= 0) {
      await dispatch(fetchCalendarEvents(projectId));
    }
    return { success: true, ...result };
  } catch (err: any) {
    debug.warn('syncAppleCalendar failed', err);
    return { success: false, message: err?.message };
  }
};
